import { classes, style, media, keyframes } from 'typestyle';
import { RED_PRIMARY, TEAL_PRIMARY } from 'src/utils/Style/Theme';
import { important, px, rem } from 'csx';

const NAVBAR_RIGHT_MIN_WIDTH = 175;
const NAVBAR_HORIZONTAL_PADDING = 10;
const spinnerFrames = keyframes({
  '0%': {
    transform: 'rotate(0deg)',
  },
  '100%': {
    transform: 'rotate(360deg)',
  },
});
const headerbarStyle = style(
  media(
    { type: 'print' },
    {
      $nest: {
        '.navbar-left': {
          // all the other stuff except the scope text is hidden in print mode
          maxWidth: '100%',
        },
      },
    },
  ),
  {
    $debugName: 'headerbarStyle',
    minHeight: px(30),
    padding: `0 ${NAVBAR_HORIZONTAL_PADDING}px`,
    backgroundColor: '#EFF0F0',
    fontSize: '12px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    $nest: {
      '.nav': {
        flexDirection: 'row',
      },
      '.navbar-nav': {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        $nest: {
          li: {
            // paddingLeft: 3,
            marginRight: rem(0.3),
          },
        },
      },
      '#headerbar-print > i': {
        paddingRight: 5,
      },
      '#headerbar-reconcile > i': {
        paddingRight: 5,
      },
      '.navbar-left': {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        padding: px(4),
        columnGap: px(5),
        $nest: {
          '.high-visibility': {
            color: 'red',
          },
        },
      },
      '.navbar-right': {
        flexShrink: 0,
      },
    },
  },
);
const leftNavBarStyle = style({
  display: 'flex',
  flexWrap: 'wrap',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  flexDirection: 'row',
  alignItems: 'center',
  marginLeft: 'auto',
  $nest: {
    button: {
      lineHeight: 1.5,
      fontFamily: '"Open Sans", sans-serif',
    },
    li: {
      marginRight: rem(0.3),
    },
    '& #Cart .s5-MuiIcon-root': {
      width: ' 1rem'
    }
  },
});
// importants below are needed to override Material UI Link styles
const iconLink = style(
  media(
    {
      type: 'print',
    },
    {
      display: 'none',
    },
  ),
  {
    $debugName: 'iconLink',
    display: 'flex',
    alignItems: 'baseline',
    color: important('#3A3A3E'),
    marginLeft: important(px(3)),
    padding: important('0 0.2rem'),
    textUnderlineOffset: px(3),
    $nest: {
      '&:hover': {
        color: important('#005c54'),
      },
      '&.disabled:hover': {
        color: important('gray'),
      },
      '&.clickable': {
        cursor: 'pointer',
      },
      '&.refreshable': {
        color: important(TEAL_PRIMARY),
      },
      '&.indeterminate': {
        cursor: 'not-allowed',
        color: important(RED_PRIMARY),
        textDecoration: 'none',
      },
      '&.disabled': {
        color: important('gray'),
        cursor: 'not-allowed',
      },
    },
  },
);
const printPopover = style({
  $nest: {
    '.s5-MuiPopover-paper': {
      border: '1px solid rgba(0,0,0,.2)',
    },
    '.s5-MuiPaper-elevation8': {
      boxShadow: 'none',
    },
    '.s5-MuiTypography-body1': {
      padding: '0.5rem 0.75rem',
      marginBottom: 0,
      fontSize: '1rem',
      backgroundColor: '#f7f7f7',
      borderBottom: '1px solid #ebebeb',
    },
  },
});

const filterPopover = style({
  pointerEvents: 'none',
  $nest: {
    '.s5-MuiPaper-elevation8': {
      boxShadow: 'none',
    },
    '.s5-MuiPopover-paper': {
      pointerEvents: 'auto',
      maxHeight: `calc(100vh - 150px)`,
      paddingLeft: 0,
      minWidth: 175,
      overflow: 'auto',
      marginTop: '0.45rem',
      border: '1px solid rgba(0,0,0,.2)',
      $nest: {
        '.s5-MuiTypography-body1': {
          padding: '0.5rem 0.75rem',
          marginBottom: 0,
          fontSize: '1rem',
          backgroundColor: '#f7f7f7',
          borderBottom: '1px solid #ebebeb',
          color: 'black',
        },
        ul: {
          marginBottom: 'auto',
          listStyleType: 'disc',
          width: '100%',
          margin: 0,
          color: 'black',
          fontSize: '0.87rem',
          padding: '0.5rem 1.5rem',

          $nest: {
            li: {
              margin: 'auto',
            },
            '.list-popover-container': {
              display: 'flex',
              justifyContent: 'space-between',
            },
          },
        },
      },
    },
  },
});

const iconRightSpace = style({
  $debugName: 'iconRightSpace',
  marginRight: '0.25rem',
});

const selectorDropDown = style({
  listStyleType: 'none',
  width: '100%',
  margin: 0,
  paddingLeft: 0,
  $nest: {
    li: {
      width: '100%',
      height: 30,
      padding: 5,
      cursor: 'pointer',
      $nest: {
        '&:hover': {
          background: '#eee',
        },
      },
    },
  },
});

const smallSpinner = style({
  display: 'inline-block',
  width: 12,
  height: 12,
  $nest: {
    '&::after': {
      content: '" "',
      display: 'block',
      width: 10,
      height: 10,
      margin: '0px',
      border: '2px solid #898989',
      borderRadius: '50%',
      animationName: spinnerFrames,
      animationDuration: '1.2s',
      animationIterationCount: 'infinite',
      animationTimingFunction: 'linear',
      borderColor: '#898989 transparent #898989 transparent',
    },
  },
});

const iconSpin = keyframes({
  to: { transform: 'rotate(180deg)' },
});

export const agentButton = style({
  $debugName: 'agentButton',
  background: 'linear-gradient(to right, #68D2D1, #985ADB)',
  color: important('white'),
  borderRadius: important(px(16)),
  padding: important('2px 12px'),
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  textDecoration: important('none'),
  transition:
    'background 520ms ease, color 520ms ease, opacity 520ms ease, filter 520ms ease, box-shadow 520ms ease',
  $nest: {
    '&, & *': {
      textDecoration: important('none'),
    },
    '& i, & svg, & .s5-MuiIcon-root': {
      color: important('inherit'),
      fill: 'currentColor',
    },
    '&:hover': {
      background: 'linear-gradient(to right, #985ADB, #68D2D1)',
      color: important('#6b6f75'),
      boxShadow:
        'inset 0 0 0 9999px rgba(255,255,255,0.35), 0 2px 8px rgba(0,0,0,0.15)',
      filter: 'saturate(0.7)',
      opacity: 0.95,
    },
    '&:hover i, &:hover svg, &:hover .s5-MuiIcon-root': {
      animation: `${iconSpin} 650ms ease-in-out 1`,
    },
    '&:active': {
      opacity: 0.9,
      filter: 'saturate(0.6)',
      boxShadow:
        'inset 0 0 0 9999px rgba(255,255,255,0.4), 0 1px 4px rgba(0,0,0,0.2)',
    },
    'span': {
      fontSize: '14px',
      fontWeight: 700,
    },
  },
});


export function buildFilterIconClass(shouldFiltersBeEnabled: boolean) {
  let possibleClass;
  if (shouldFiltersBeEnabled) {
    possibleClass = style({
      $debugName: 'activeFilter',
      color: TEAL_PRIMARY,
    });
  }
  return classes('fas fa-filter', iconRightSpace, possibleClass);
}

export default {
  headerbarStyle,
  leftNavBarStyle,
  iconLink,
  filterPopover,
  iconRightSpace,
  selectorDropDown,
  smallSpinner,
  printPopover,
  agentButton,
};
